// this is a page for marketing/sales purposes
// users can select what type of document they want to OCR
// and we give them back the correction screen along with the data

import { Publish } from '@mui/icons-material'
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from '@mui/material'
import { AxiosResponse } from 'axios'
import { useCallback, useState } from 'react'
import Dropzone, { DropzoneState } from 'react-dropzone'
import {
	SignalTypeIds,
	StartedWorkflowModel,
	WorkflowTaskEventAction,
	WorkflowTaskWorkflowEventData,
} from '../../api/clients/workflows/DTOs'
import TagsClient from '../../api/clients/workflows/TagsClient'
import WorkflowsClient from '../../api/clients/workflows/WorkflowsClient'
import { OcrCorrectionPageWrapper } from '../../modules/Ocr/OcrCorrectionPageWrapper'
import { usePrompts } from '../../services/notifications/PromptService'
import { toastService } from '../../services/notifications/ToastService'
import { uploadBlob } from '../../utils/BlobUtils'
import { CompletedDemoDialog } from './CompletedDemoDialog'

export const InvoiceOcrDemoPage = () => {
	const [workflowTaskId, setWorkflowTaskId] = useState<number>()
	const [dialogOpen, setDialogOpen] = useState(true)
	const [isRunningWorkflow, setIsRunningWorkflow] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)

	const promptService = usePrompts()
	const workflowsClient = new WorkflowsClient()

	const handleRunOcr = useCallback(async (blobId: number) => {
		setIsRunningWorkflow(true)

		const workflowTagsClient = new TagsClient()

		const { data: workflowDefinitions } =
			await workflowTagsClient.queryWorkflows([
				{
					class: 'KeyMarkLabs.BlobStorage.Abstractions.Models.BlobRecord.Name',
					value: 'invoice-demo',
				},
			])

		const workflowDefinition = workflowDefinitions[0]

		const createWorkflowDefinitionWithRetry = async (
			workflowDefinitionId: number,
			maxRetries = 3,
			timeout = 10000,
		) => {
			let attempts = 0

			while (attempts < maxRetries) {
				attempts++

				try {
					const response = await Promise.race([
						workflowsClient.createWorkflowSignal(
							workflowDefinitionId,
							SignalTypeIds.DocumentUploaded,
							[
								{ type: 'DocumentType', value: 'invoice' },
								{ type: 'Blob', value: blobId },
							],
						),
						new Promise<AxiosResponse<StartedWorkflowModel>>((_, reject) =>
							setTimeout(() => reject(new Error('Timeout')), timeout),
						),
					])

					return response.data
				} catch (e) {
					if (attempts >= maxRetries) {
						console.error(e)
						return
					}
					console.log(`Attempt ${attempts} failed, retrying...`)
				}
			}
		}

		const startedWorkflowModel = await createWorkflowDefinitionWithRetry(
			workflowDefinition.id,
		)

		if (startedWorkflowModel === undefined) {
			toastService.displayToast({
				message: 'Failed to start OCR workflow. Please refresh and try again',
				severity: 'error',
				area: 'global',
			})

			return
		}

		const interval = setInterval(async () => {
			const { data: status } = await workflowsClient.getWorkflowStatus(
				startedWorkflowModel.workflowInstanceId,
			)

			const workflowTasks = status.workflowEvents
				.filter((v) => v.eventType === 'WorkflowTask')
				.map((v) => v.eventData as WorkflowTaskWorkflowEventData)

			if (workflowTasks.length > 0) {
				console.log('clearing interval')
				window.clearInterval(interval)
			}

			const workflowTask = workflowTasks[0]

			if (workflowTask.action === WorkflowTaskEventAction.Created) {
				setIsRunningWorkflow(false)
				setWorkflowTaskId(workflowTask.workflowTaskId)
			}
		}, 5000)
	}, [])

	if (workflowTaskId !== undefined)
		return (
			<OcrCorrectionPageWrapper
				onDocumentReviewed={(outcome, comparedValues, obj) => {
					// todo - send email ???
					promptService
						.showDialog((props) => (
							<CompletedDemoDialog
								onConfirm={() => props.close('okay')}
								onClose={() => props.close('cancel')}
							/>
						))
						.then(async (v) => {
							if (v === 'okay') setWorkflowTaskId(undefined)
						})
				}}
				isSubmitting={isSubmitting}
				workflowTaskId={workflowTaskId}
			/>
		)

	return (
		<Box>
			<DocumentUploadDialog
				open={dialogOpen}
				onRunOcr={handleRunOcr}
				isLoading={isRunningWorkflow}
			/>
		</Box>
	)
}

type DocumentUploadDialogProps = {
	open: boolean
	onRunOcr: (blobId: number) => void
	isLoading: boolean
}

const DocumentUploadDialog = ({
	open,
	onRunOcr,
	isLoading,
}: DocumentUploadDialogProps) => {
	const [file, setFile] = useState<File>()
	const [isUploading, setIsUploading] = useState(false)

	const fileReader = new FileReader()

	const handleDrop = useCallback(async (acceptedFiles: File[]) => {
		let droppedFile = acceptedFiles[0]

		try {
			fileReader.readAsArrayBuffer(droppedFile as Blob)
		} catch (e) {
			console.error(e)

			throw new Error('error reading file as array buffer')
		}

		const type = droppedFile.type
		if (type === 'image/jpg' || type === 'image/png')
			droppedFile = await convertImageToPDF(droppedFile, type)

		setFile(droppedFile)
	}, [])

	async function convertImageToPDF(
		imageFile: File,
		imageType: string,
	): Promise<File> {
		const reader = new FileReader()

		return new Promise((resolve, reject) => {
			reader.onload = async function (e) {
				const { jsPDF } = await import('jspdf')

				const img = new Image()
				img.src = e.target?.result as string

				img.onload = function () {
					// Create a canvas to draw the image
					const canvas = document.createElement('canvas')
					canvas.width = img.width
					canvas.height = img.height

					const ctx = canvas.getContext('2d')
					ctx?.drawImage(img, 0, 0, img.width, img.height)

					// Create a jsPDF instance
					const pdf = new jsPDF({
						orientation: img.width > img.height ? 'landscape' : 'portrait',
						unit: 'px',
						format: [img.width, img.height],
					})

					const imgData = canvas.toDataURL(imageType) // Convert canvas to base64 image data
					pdf.addImage(imgData, 'JPEG', 0, 0, img.width, img.height)

					// Convert the PDF to a Blob
					const pdfBlob = pdf.output('blob')

					// Convert the Blob to a File
					const pdfFile = new File([pdfBlob], imageFile.name, {
						type: 'application/pdf',
					})

					// Resolve the promise with the created File object
					resolve(pdfFile)
				}

				img.onerror = function () {
					reject(new Error('Failed to load the image.'))
				}
			}

			reader.onerror = function () {
				reject(new Error('Failed to read the file.'))
			}

			reader.readAsDataURL(imageFile) // Convert image to base64
		})
	}

	const handleUpload = async () => {
		if (file === undefined) return

		setIsUploading(true)

		// upload the blob (in chunks if necessary)
		const blobId = await uploadBlob(fileReader, file, file.name).finally(() =>
			setIsUploading(false),
		)

		if (blobId === undefined) throw new Error('error uploading blob')

		onRunOcr(blobId)
	}

	return (
		<Dialog open={open} maxWidth="sm" fullWidth>
			<DialogTitle align="center">Forms inMotion Invoice OCR Demo</DialogTitle>
			<DialogContent>
				<Box display="flex" flexDirection="column" gap={2}>
					<Typography variant="body1">
						Test out Forms inMotion's optical character recognition capabilties
						by uploading an invoice.
					</Typography>

					<Dropzone
						maxFiles={1}
						maxSize={30000000}
						accept={['image/tiff', 'application/pdf', 'image/jpg', 'image/png']}
						multiple={false}
						onDrop={handleDrop}
						disabled={isUploading || isLoading}
					>
						{({ getRootProps, getInputProps }: DropzoneState) => (
							<Box marginY={2} display="flex" justifyContent="center">
								<div {...getRootProps()}>
									<input {...getInputProps()} />
									<Box
										display="flex"
										flexDirection="column"
										justifyContent="center"
										alignItems="center"
										borderRadius={4}
										border="dashed"
										borderColor="text.secondary"
										sx={{
											cursor: isLoading ? 'default' : 'pointer',
										}}
										minWidth="500px"
										minHeight="200px"
									>
										{isUploading || isLoading ? (
											<CircularProgress />
										) : file !== undefined ? (
											<Typography>{file.name}</Typography>
										) : (
											<>
												<Typography>
													Drag and drop your file here, or click to select a
													file
												</Typography>
												<Publish />
											</>
										)}
									</Box>
								</div>
							</Box>
						)}
					</Dropzone>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button
					variant="contained"
					disabled={file === undefined || isUploading || isLoading}
					onClick={handleUpload}
				>
					Run OCR
				</Button>
			</DialogActions>
		</Dialog>
	)
}
